import React, { useEffect, useState } from 'react';
import {
  aopApi, agentForWorkflow, agentLabel, formatDateTime, formatTime,
  formatTokens, prettyActionType, runDuration, workflowLabel,
} from '../aopApi';

const EVENT_COLORS = {
  workflow: 'var(--blue)',
  agent: 'var(--purple)',
  tool: 'var(--text-sec)',
  approval: 'var(--orange)',
  report: 'var(--green)',
  job: 'var(--text-dim)',
  figma: 'var(--agent-tim)',
};

const eventColor = (type) => {
  if (type.includes('failed')) return 'var(--red)';
  if (type.includes('completed') || type.includes('decided')) return 'var(--green)';
  return EVENT_COLORS[type.split('.')[0]] || 'var(--text-dim)';
};

function StepIcon({ status }) {
  const styles = {
    completed: { background: 'rgba(52,199,89,0.15)', color: 'var(--green)', label: '✓' },
    running: { background: 'rgba(74,143,247,0.15)', color: 'var(--blue)', label: '▶' },
    failed: { background: 'rgba(255,98,93,0.15)', color: 'var(--red)', label: '✕' },
    queued: { background: 'rgba(156,163,197,0.12)', color: 'var(--text-sec)', label: '○' },
  };
  const s = styles[status] || styles.queued;
  return (
    <div className="aop-trace-step-icon" style={{ background: s.background, color: s.color }}>
      {s.label}
    </div>
  );
}

export default function WorkflowRunDetail({ runId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!runId) return;
    setLoading(true);
    aopApi
      .run(runId)
      .then((res) => {
        if (!res.success) throw new Error(res.error || 'Failed to load run');
        setData(res);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [runId]);

  if (loading) return <div className="aop-empty">Loading run…</div>;
  if (error || !data) {
    return (
      <div>
        <div className="aop-breadcrumb">
          <span className="link" onClick={onBack}>Workflow Runs</span>
        </div>
        <div className="aop-empty">
          <div className="aop-empty-icon">✕</div>
          {error || 'Run not found'}
        </div>
      </div>
    );
  }

  const { run, agentRuns = [], auditEvents = [], pendingActions = [] } = data;
  const agent = agentForWorkflow(run.workflow);
  const name = workflowLabel(run.workflow);

  // Step list: completed steps from the checkpoint, plus the in-flight or
  // failed step if the run is not complete.
  const steps = (run.completedSteps || []).map((s) => ({ name: s, status: 'completed' }));
  if (run.status !== 'completed' && run.currentStep && !(run.completedSteps || []).includes(run.currentStep)) {
    steps.push({ name: run.currentStep, status: run.status === 'failed' ? 'failed' : 'running' });
  }

  return (
    <div>
      <div className="aop-breadcrumb">
        <span className="link" onClick={onBack}>Workflow Runs</span>
        <span className="sep">›</span>
        <span className="current">{name}</span>
      </div>

      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">{name}</h1>
          <p className="aop-page-subtitle">Step-by-step execution trace</p>
        </div>
        <div className="aop-page-actions">
          <button className="aop-btn ghost" onClick={onBack}>← Back</button>
        </div>
      </div>

      {/* Run metadata */}
      <div className="aop-run-meta">
        <div className="aop-run-meta-item">
          <div className="aop-run-meta-label">Agent</div>
          <div className="aop-run-meta-value">
            <span className={`aop-agent-tag ${agent}`}>{agentLabel(agent)}</span>
          </div>
        </div>
        <div className="aop-run-meta-item">
          <div className="aop-run-meta-label">Status</div>
          <div className="aop-run-meta-value"><span className={`aop-badge ${run.status}`}>{run.status}</span></div>
        </div>
        <div className="aop-run-meta-item">
          <div className="aop-run-meta-label">Trigger</div>
          <div className="aop-run-meta-value">{run.triggerType}</div>
        </div>
        <div className="aop-run-meta-item">
          <div className="aop-run-meta-label">Started</div>
          <div className="aop-run-meta-value" style={{ fontSize: 12 }}>{formatDateTime(run.startedAt || run.createdAt)}</div>
        </div>
        <div className="aop-run-meta-item">
          <div className="aop-run-meta-label">Duration</div>
          <div className="aop-run-meta-value">{runDuration(run)}</div>
        </div>
      </div>

      {run.error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {run.error}
        </div>
      )}

      {/* Step trace */}
      <div className="aop-card" style={{ marginBottom: 20 }}>
        <div className="aop-card-header">
          <span className="aop-card-title">Steps</span>
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {(run.completedSteps || []).length} step{(run.completedSteps || []).length === 1 ? '' : 's'} completed
          </span>
        </div>
        <div style={{ padding: '12px 20px' }}>
          {steps.length === 0 ? (
            <div className="aop-empty">No steps recorded yet</div>
          ) : steps.map((step, i) => (
            <div key={step.name} className="aop-trace-step">
              <div className="aop-trace-step-line-col">
                <StepIcon status={step.status} />
                {i < steps.length - 1 && <div className="aop-trace-step-connector" />}
              </div>
              <div className="aop-trace-step-body">
                <div className="aop-trace-step-name">
                  {step.name}
                  {step.status === 'running' && (
                    <span style={{ fontSize: 10, color: 'var(--blue)', marginLeft: 8 }}>running…</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Agent runs */}
      {agentRuns.length > 0 && (
        <div className="aop-card" style={{ marginBottom: 20 }}>
          <div className="aop-card-header">
            <span className="aop-card-title">Agent Runs</span>
          </div>
          <table className="aop-table">
            <thead>
              <tr><th>Agent</th><th>Model</th><th>Provider</th><th>Skill Version</th><th>Tokens</th><th>Latency</th><th>Status</th></tr>
            </thead>
            <tbody>
              {agentRuns.map((ar) => (
                <tr key={ar._id} style={{ cursor: 'default' }}>
                  <td><span className={`aop-agent-tag ${ar.agent}`}>{agentLabel(ar.agent)}</span></td>
                  <td><span className="text-muted">{ar.model}</span></td>
                  <td><span className="text-muted">{ar.provider}</span></td>
                  <td><span className="text-muted" style={{ fontFamily: 'monospace', fontSize: 10 }}>{String(ar.skillVersion || '').slice(0, 12)}</span></td>
                  <td><span className="text-muted">{formatTokens(ar.tokenUsage && ar.tokenUsage.total)}</span></td>
                  <td><span className="text-muted">{ar.latencyMs != null ? `${ar.latencyMs}ms` : '—'}</span></td>
                  <td><span className={`aop-badge ${ar.status}`}>{ar.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending actions raised by this run */}
      {pendingActions.length > 0 && (
        <div className="aop-card" style={{ marginBottom: 20 }}>
          <div className="aop-card-header">
            <span className="aop-card-title">Pending Actions Raised</span>
          </div>
          <table className="aop-table">
            <thead>
              <tr><th>Action</th><th>Target</th><th>Risk</th><th>Status</th></tr>
            </thead>
            <tbody>
              {pendingActions.map((pa) => (
                <tr key={pa._id} style={{ cursor: 'default' }}>
                  <td>
                    <div className="cell-primary">{prettyActionType(pa.actionType)}</div>
                    <div className="cell-sub">{pa.summary || ''}</div>
                  </td>
                  <td><span className="text-muted">{pa.targetSystem}</span></td>
                  <td><span className={`aop-badge ${pa.riskLevel}`}>{String(pa.riskLevel || '').toUpperCase()}</span></td>
                  <td><span className={`aop-badge ${pa.status}`}>{pa.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Full event trace */}
      <div className="aop-card">
        <div className="aop-card-header">
          <span className="aop-card-title">Event Trace</span>
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{auditEvents.length} events</span>
        </div>
        <div style={{ padding: '8px 0' }}>
          {auditEvents.map((ev, i) => (
            <div key={ev._id} className="aop-audit-event">
              <div className="aop-audit-dot-col">
                <div className="aop-audit-dot" style={{ background: eventColor(ev.type) }} />
                {i < auditEvents.length - 1 && <div className="aop-audit-line" />}
              </div>
              <div className="aop-audit-body">
                <div className="aop-audit-desc">{ev.message}</div>
                <div className="aop-audit-meta">
                  <span>{formatTime(ev.createdAt)}</span>
                  <span style={{ color: eventColor(ev.type) }}>{ev.type}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
